import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Globe, KeyRound, Terminal, ScrollText, Inbox, Send, Mail, LogOut, Shield, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DeliveryStatus } from "@/components/DeliveryStatus";

const NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Overview", id: "nav-overview" },
  { to: "/app/domains", icon: Globe, label: "Domains & DNS", id: "nav-domains" },
  { to: "/app/credentials", icon: KeyRound, label: "SMTP Credentials", id: "nav-credentials" },
  { to: "/app/api-keys", icon: Terminal, label: "API Keys", id: "nav-api-keys" },
  { to: "/app/logs", icon: ScrollText, label: "Activity Logs", id: "nav-logs" },
  { to: "/app/inbox", icon: Inbox, label: "Inbound Mailbox", id: "nav-inbox" },
  { to: "/app/console", icon: Send, label: "Test Console", id: "nav-console" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const doLogout = () => { logout(); navigate("/"); };

  return (
    <div className="flex min-h-screen bg-[#0B0F17] text-white">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-800 bg-[#0B0F17] lg:flex">
        <Link to="/" className="flex items-center gap-2 border-b border-slate-800 px-6 py-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 text-[#04120c]"><Mail className="h-5 w-5" /></div>
          <span className="display text-lg font-bold">RelayMX</span>
        </Link>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-emerald-500/10 text-emerald-400" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"}`}>
              <n.icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} /> {n.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-amber-400 transition-colors hover:bg-amber-500/10">
              <Shield style={{ width: 18, height: 18 }} /> Admin Panel
            </NavLink>
          )}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <div className="flex items-center gap-3 rounded-lg px-3 py-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-800 text-sm font-semibold text-emerald-400">
              {(user?.name || user?.email || "?")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{user?.name}</div>
              <div className="truncate text-xs text-slate-500">{user?.email}</div>
            </div>
            <button onClick={doLogout} data-testid="logout-button" className="text-slate-500 transition-colors hover:text-red-400"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="min-w-0 flex-1 lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-800 bg-[#0B0F17]/80 px-6 py-3.5 backdrop-blur-xl">
          <div className="flex items-center gap-2 lg:hidden">
            <button data-testid="mobile-navigation-toggle" aria-label="Toggle navigation" aria-expanded={menu} onClick={() => setMenu(!menu)} className="p-1 text-emerald-400">{menu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
            <span className="display font-bold">RelayMX</span>
          </div>
          <div className="mono hidden items-center gap-2 text-xs text-slate-500 lg:flex">
            <DeliveryStatus compact />
          </div>
          <Link to="/app/console" data-testid="topbar-send-test" className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-3.5 py-2 text-sm font-semibold text-[#04120c] transition-colors hover:bg-emerald-400">
            <Send className="h-4 w-4" /> Send test email
          </Link>
        </header>
        {menu && <nav data-testid="mobile-navigation" className="grid grid-cols-2 gap-2 border-b border-slate-800 p-4 lg:hidden">
          {NAV.map(n => <NavLink data-testid={`mobile-${n.id}`} key={n.id} to={n.to} end={n.end} onClick={() => setMenu(false)} className={({ isActive }) => `rounded-lg px-3 py-2 text-sm ${isActive ? "bg-emerald-500/10 text-emerald-400" : "text-slate-400"}`}>{n.label}</NavLink>)}
          {user?.role === "admin" && <Link data-testid="mobile-nav-admin" to="/admin" className="px-3 py-2 text-sm text-amber-300">Admin panel</Link>}
          <button data-testid="mobile-logout-button" onClick={doLogout} className="px-3 py-2 text-left text-sm text-red-400">Sign out</button>
        </nav>}
        <main className="mx-auto max-w-6xl p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
